import { Backdrop, Button, CircularProgress, Container, IconButton } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';
import {
    Add as AddIcon, Cancel as CancelIcon, Delete as DeleteIcon, Save as SaveIcon
} from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { UseTranslationResponse, useTranslation } from 'react-i18next';

import * as Yup from 'yup';
import CheckBox from "../../components/Form/Checkbox";
import Select from '../../components/Form/Select';
import SelectShedMovementType from '../../components/Form/SelectShedMovementType';
import SelectSupplier from '../../components/Form/SelectSupplier';
import TextField from '../../components/Form/TextField';
import AlertDialog from '../../components/ui/AlertDialog';
import ButtonUI from '../../components/ui/ButtonUI';
import LoadingUI from '../../components/ui/LoadingUI';
import PageTitle from '../../components/ui/PageTitleUI';
import { useAuth } from '../../hooks/auth';
import { WindowPost, WindowPut, WindowShed } from '../../interfaces/Window';
import WindowApi from '../../services/WindowApi';
import { useStyles } from './WindowFormStyle';

type Props = {
    modelId: number,
    traduction: UseTranslationResponse,
    perimeterList: any,
    clickHandlerClose?: () => void,
    handleSetSuccess?: (value: boolean) => void,
}

interface IFormPanel {
    perimeter: any;
    supplier: any;
    hour: any;
    weekNameSUN: any;
    weekNameMON: any;
    weekNameTUE: any;
    weekNameWED: any;
    weekNameTHU: any;
    weekNameFRI: any;
    weekNameSAT: any;
    shed: any;
    movementType: any;
    startTime: any;
    tolerance: any;
    platform: any;
}

const WindowForm: React.FC<Props> = (props) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const { t: _t } = useTranslation("General");
    const { t } = useTranslation("Window");
    const { t: tval } = useTranslation("Validation");
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const refFieldStartTime = useRef<HTMLInputElement>(null);
    const refShedMovType = useRef<HTMLInputElement>(null);

    const [windowShedList, setWindowShedList] = useState<WindowShed[]>([]);

    //Custom error message:
    const [shedsErrorMessage, setShedsErrorMessage] = useState<string | undefined>("");

    const { handleSubmit, reset, control, getValues, setError, resetField, setValue, clearErrors } = useForm<IFormPanel>({
        defaultValues: {
            perimeter: props.perimeterList[0], supplier: {}, hour: '',
            weekNameSUN: false, weekNameMON: false,
            weekNameTUE: false, weekNameWED: false,
            weekNameTHU: false, weekNameFRI: false,
            weekNameSAT: false, shed: {}, movementType: {},
            startTime: '', tolerance: '', platform: ''
        }
    });

    const shedListColumns = [
        {
            name: 'shedCode',
            label: String(t('shedCode')),
            options: {
                // setCellProps: () => ({
                //     align: "center"
                // }),
            }
        },
        {
            name: 'shedType',
            label: String(t('shedType')),
            options: {
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return value === 'D' ? t('unload') : t('load');
                }
            }
        },
        {
            name: 'movementTypeDescription',
            label: String(t('movementTypeDescription'))
        },
        {
            name: 'startTime',
            label: String(t('startTime'))
        },
        {
            name: 'tolerance',
            label: String(t('tolerance'))
        },
        {
            name: 'platform',
            label: String(t('platform'))
        },
        {
            name: "shedCode",
            label: " ",
            options: {
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
                    renderGridButtons(value)
                )
            }
        },
    ]

    const renderGridButtons = (shedCode: any) => {
        return (
            <AlertDialog
                dialogTitle={_t("dialogDeleteTitle")}
                dialogContentText={_t("dialogDeleteContentText")}
                cancelTextButton={_t("cancel")}
                confirmTextButton={_t("confirm")}
                confirmDialogFunction={() => { handleDeleteShed(shedCode); }}
                Button={
                    <IconButton size='small' edge="end" >
                        <DeleteIcon />
                    </IconButton>
                }
            />
        );
    }

    const gridOptions = {
        // sortOrder: { name: sort.split(':')[0], direction: sort.split(':')[1] } as any,
        textLabels: {
            body: {
                noMatch: _t('noMatch'),
                toolTip: "",
            },
            pagination: {
                next: _t("next"),
                previous: _t("previous"),
                rowsPerPage: _t("rowsPerPage") + ":",
                displayRows: _t("of"),
            }
        }
    }

    //Personalizar aparencia da grid de galpoes
    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: windowShedList.length > 0 ? 'pointer' : ''
                }
            }
        }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        getModel();
    }, [props.modelId]);

    //Carregar a Janela
    const getModel = useCallback(() => {

        setWindowShedList([]);

        if (props.modelId !== 0) {
            setLoading(true)

            WindowApi.get(props.modelId).then((result) => {
                if (result) {
                    setWindowShedList(result.windowSheds);

                    var splitedhourConcierge = result.hourConcierge.split(':')

                    reset({
                        perimeter: { code: result.perimeter, label: result.perimeter },
                        supplier: { code: result.supplierId, label: `${result.supplierId} - ${result.supplierName}`, name: result.supplierName },
                        hour: splitedhourConcierge[0] + ':' + splitedhourConcierge[1],
                        weekNameSUN: result.flagSunday,
                        weekNameMON: result.flagMonday,
                        weekNameTUE: result.flagTuesday,
                        weekNameWED: result.flagWednesday,
                        weekNameTHU: result.flagThursday,
                        weekNameFRI: result.flagFriday,
                        weekNameSAT: result.flagSaturday,
                        shed: {},
                        movementType: {}
                    })
                }
                else {
                    reset({
                        perimeter: {},
                        supplier: {},
                        hour: '',
                        weekNameSUN: false,
                        weekNameMON: false,
                        weekNameTUE: false,
                        weekNameWED: false,
                        weekNameTHU: false,
                        weekNameFRI: false,
                        weekNameSAT: false,
                        shed: {},
                        movementType: {}

                    })
                }
            }).catch(() => {
            }).finally(() => {
                setLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.modelId])

    const yupValidationAddNewItemOnList = async (data: any) => {
        clearErrors(["shed", "movementType", "startTime", "tolerance", "platform"])
        try {
            const schema = Yup.object().shape({
                shed: Yup.object().nullable()
                    .required(tval("requiredField"))
                    .shape({ code: Yup.string().required(tval("requiredField")) }),
                movementType: Yup.object().nullable()
                    .required(tval("requiredField"))
                    .shape({ code: Yup.string().required(tval("requiredField")) }),
                startTime: Yup.string()
                    .required(tval("requiredField"))
                    .length(5, tval("requiredLength5"))
                    .matches(/([01][0-9]|2[0-3]):[0-5][0-9]/, tval("formatTime24h")),
                tolerance: Yup.string()
                    .required(tval("requiredField"))
                    .length(5, tval("requiredLength5"))
                    .matches(/([0-9][0-9]):[0-5][0-9]/, tval("formatTime24h")),
                platform: Yup.string()
                    .min(1, tval("minLength"))
                    .required(tval("requiredField")),
            })

            await schema.validate(data, {
                abortEarly: false,
            })

        } catch (err) {

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    setError(error.path?.split('.')[0] as "shed" | "movementType" | "startTime" | "tolerance" | "platform", { message: error.message })
                })
            }

            return false
        }

        return true
    }

    const getEndTime = (startTime: string, tolerance: string) => {
        var startTimeHours = parseInt(startTime.split(':')[0])
        var startTimeMinutes = parseInt(startTime.split(':')[1])

        var toleranceHours = parseInt(tolerance.split(':')[0])
        var toleranceMinutes = parseInt(tolerance.split(':')[1])

        var endTimeDays = 0
        var endTimeHours = 0
        var endTimeMinutes = 0

        if (toleranceHours + toleranceMinutes >= 60) {
            endTimeHours = 1
            endTimeMinutes = (startTimeMinutes + toleranceMinutes) - 60
        }
        else {
            endTimeMinutes = startTimeMinutes + toleranceMinutes
        }

        if (startTimeHours + toleranceHours + endTimeHours >= 24) {
            endTimeDays = 1
            endTimeHours = endTimeHours + ((startTimeHours + toleranceHours) - 24)
        }
        else {
            endTimeHours = endTimeHours + (startTimeHours + toleranceHours)
        }

        //Se algum calculo deu mais minutos que 1h (dificil mas, vai que), deve ajustar a hora para dar um TimeSpan valido:
        if (endTimeMinutes >= 60) {
            endTimeHours += 1;
            endTimeMinutes -= 60;
        }

        if (endTimeHours === 24 && endTimeMinutes === 0) {
            endTimeHours = 23;
            endTimeMinutes = 59;
        }
        return endTimeDays.toString() + '.' + endTimeHours.toString().padStart(2, '0') + ':' + endTimeMinutes.toString().padStart(2, '0') + ':00'
    }

    const handleIncludeItem = async () => {
        var formData = getValues()

        if (!(await yupValidationAddNewItemOnList(formData)))
            return

        //Adicionar:
        if (windowShedList.filter((item: WindowShed) => { return item.shedId === formData.shed.code }).length === 1) {
            setShedsErrorMessage(t('validationIncludedWindowShed'))
            return;
        }

        setShedsErrorMessage('')

        resetField('shed')
        resetField('movementType')
        resetField('startTime', { defaultValue: '' })
        resetField('tolerance', { defaultValue: '' })
        resetField('platform', { defaultValue: '' })

        let data: WindowShed = {
            windowId: props.modelId,
            shedId: formData.shed.code,
            shedCode: formData.shed.shedCode,
            shedType: formData.shed.shedType,
            movementTypeId: formData.movementType.code,
            movementTypeDescription: formData.movementType.label,
            startTime: formData.startTime,
            tolerance: formData.tolerance,
            platform: formData.platform,
            orderShed: windowShedList.length + 1,
            endTime: getEndTime(formData.startTime, formData.tolerance)
        }

        setWindowShedList([data, ...windowShedList]);
        refShedMovType.current?.focus();

    }

    const handleDeleteShed = (shedCode: string) => {
        setWindowShedList(windowShedList.filter((item: WindowShed) => { return item.shedCode !== shedCode }));
    }

    const onSubmit = async (data: any) => {
        debugger
        if (!(await yupValidationSaveForm(data)))
            return

        let saveData = {
            id: props.modelId,
            companyId: user.company.companyID,
            flagWindowType: 'E',
            flagTypeWindow: 'E',
            perimeter: data.perimeter.code,
            supplierId: data.supplier.code,
            supplierName: data.supplier.name,
            hourConcierge: data.hour,
            createdAt: '',
            updatedBy: user.userName,
            updatedAt: new Date(),
            flagSunday: data.weekNameSUN,
            flagMonday: data.weekNameMON,
            flagTuesday: data.weekNameTUE,
            flagWednesday: data.weekNameWED,
            flagThursday: data.weekNameTHU,
            flagFriday: data.weekNameFRI,
            flagSaturday: data.weekNameSAT,
            windowSheds: windowShedList
        }

        setOpenBackdrop(true);

        if (props.modelId === 0) {
            let postData: WindowPost = {
                ...saveData
            };

            WindowApi.post(postData).then(result => {
                enqueueSnackbar(_t("returnSubmitAddFormSuccess"), { variant: 'success' });
                if (props && props.handleSetSuccess)
                    props.handleSetSuccess(true);
                if (props && props.clickHandlerClose) props.clickHandlerClose();

                setWindowShedList([])
                reset()
            }).finally(() => {
                setOpenBackdrop(false);
            });
        }
        else {
            let putData: WindowPut = {
                ...saveData
            };

            WindowApi.put(putData).then(result => {
                enqueueSnackbar(_t("returnSubmitEditFormSuccess"), { variant: 'success' });
                if (props && props.handleSetSuccess)
                    props.handleSetSuccess(true);
                if (props && props.clickHandlerClose) props.clickHandlerClose();
            }).finally(() => {
                setOpenBackdrop(false);
            });
        }
    }

    const yupValidationSaveForm = async (data: any) => {
        var resp = false;
        clearErrors(["hour", "supplier", "perimeter"])
        try {
            const schema = Yup.object().shape({
                hour: Yup.string()
                    .required(tval("requiredField"))
                    .length(5, tval("requiredLength5"))
                    .matches(/([01][0-9]|2[0-3]):[0-5][0-9]/, tval("formatTime24h")),
                supplier: Yup.object().nullable().required(tval("requiredField"))
                    .shape({ code: Yup.string().required(tval("requiredField")) }),
                perimeter: Yup.object().nullable().required(tval("requiredField"))
                    .shape({ code: Yup.string().required(tval("requiredField")) })
            })

            await schema.validate(data, {
                abortEarly: false,
            })

            resp = true
        } catch (err) {

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    setError(error.path?.split('.')[0] as "perimeter" | "supplier" | "hour", { message: error.message });
                })
            }

            resp = false
        }

        if (windowShedList.length === 0) {
            setShedsErrorMessage(t('validationWindowSheds'))
            resp = false
        } else {
            setShedsErrorMessage('')
        }

        return resp
    }

    return (
        <Container maxWidth={false} component="main">

            {
                loading ?
                    <LoadingUI />
                    :
                    <React.Fragment>

                        <Backdrop className={classes.backdrop} open={openBackdrop}>
                            <CircularProgress color="inherit" />
                        </Backdrop>

                        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault() }} >
                            <Grid container spacing={1}>

                                <Grid item xs={5}>
                                    <PageTitle title={`${props.modelId === undefined ? t("titleAdd") : t("titleEdit")}`} style={{ marginLeft: "40px" }} />
                                </Grid>

                                <Grid item xs={7} style={{ display: 'flex', justifyContent: 'right' }}>
                                    <Button
                                        variant="contained"
                                        className={classes.rootCancelButton}
                                        startIcon={<CancelIcon />}
                                        onClick={() => props && props.clickHandlerClose ? props.clickHandlerClose() : null}>
                                        {_t("cancel")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        className={classes.rootButton}>
                                        {_t("save")}
                                    </Button>
                                </Grid>
                            </Grid>

                            <Grid container spacing={1}>
                                <Select
                                    label={t('perimeter')}
                                    name={'perimeter'}
                                    control={control}
                                    options={props.perimeterList}
                                    allowSelectValueNotFound={false}
                                    style={{ minWidth: 200, maxWidth: 200, marginRight: 3, marginLeft: 2 }} />


                                <SelectSupplier
                                    name={"supplier"}
                                    control={control}
                                    style={{ width: 550, marginRight: 3 }} />

                                <TextField
                                    label={t("hour")}
                                    name="hour"
                                    control={control}
                                    mask="99:99"
                                    className={classes.rootForm} />
                            </Grid>
                            <Grid container spacing={1}>

                                <Grid item xs={12} md={12}>
                                    <CheckBox label={t("weekNameSUN")} name="weekNameSUN" control={control} />
                                    <CheckBox label={t("weekNameMON")} name="weekNameMON" control={control} />
                                    <CheckBox label={t("weekNameTUE")} name="weekNameTUE" control={control} />
                                    <CheckBox label={t("weekNameWED")} name="weekNameWED" control={control} />
                                    <CheckBox label={t("weekNameTHU")} name="weekNameTHU" control={control} />
                                    <CheckBox label={t("weekNameFRI")} name="weekNameFRI" control={control} />
                                    <CheckBox label={t("weekNameSAT")} name="weekNameSAT" control={control} />
                                </Grid>
                            </Grid>

                            {/* Campos e Grid Inclusao de Galpoes */}
                            <Grid container spacing={1}>

                                <SelectShedMovementType
                                    inputRef={refShedMovType}
                                    nameSelectShed={'shed'}
                                    nameSelectMovementType={'movementType'}
                                    control={control}
                                    setValue={setValue}
                                    focusOnSelect={refFieldStartTime} />

                                <Grid item xs={4}>
                                    <TextField
                                        style={{ width: '100%', marginLeft: 12, marginRight: 12 }}
                                        label={t("startTime")}
                                        inputRef={refFieldStartTime}
                                        name="startTime"
                                        control={control}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleIncludeItem(); }}
                                        mask="99:99" />
                                </Grid>

                                <Grid item xs={2}>
                                    <TextField
                                        style={{ width: '100%', marginLeft: 12, marginRight: 12 }}
                                        label={t("tolerance")}
                                        name="tolerance"
                                        control={control}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleIncludeItem(); }}
                                        mask="99:99" />
                                </Grid>

                                <Grid item xs={1}>
                                    <TextField
                                        style={{ width: '100%', marginLeft: 12, marginRight: 12 }}
                                        label={t("platform")}
                                        name="platform"
                                        inputProps={{ maxLength: 10 }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleIncludeItem(); }}
                                        control={control} />
                                </Grid>

                                <Grid item xs={1}>
                                    <ButtonUI
                                        onClick={() => handleIncludeItem()}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleIncludeItem(); }}
                                        color="primary"
                                        variant="contained"
                                        style={{ maxWidth: '40px', maxHeight: '38px', minWidth: '40px', minHeight: '38px', marginTop: 20, marginLeft: 12 }}>
                                        <AddIcon />
                                    </ButtonUI>
                                </Grid>
                            </Grid>

                            {/* array de shedlist nao tem onde exibir erro de validacao. Exibir abaixo: */}
                            <Grid item xs={12} className='MuiFormHelperText-root Mui-error'>
                                <p style={{ color: 'red' }}>{shedsErrorMessage}</p>

                            </Grid>

                            <Grid item xs={12}>
                                <MuiThemeProvider theme={myTheme}>
                                    <MUIDataTable
                                        title=""
                                        data={windowShedList}
                                        columns={shedListColumns}
                                        options={{
                                            ...gridOptions,
                                            serverSide: false,
                                            pagination: false,
                                            sort: false,
                                            selectToolbarPlacement: "none",
                                            filter: false,
                                            search: false,
                                            download: false,
                                            print: false,
                                            viewColumns: false,
                                            selectableRows: 'none',

                                        }}
                                    />
                                </MuiThemeProvider>
                            </Grid>

                        </form>

                    </React.Fragment>
            }
        </Container >
    )
}
export default WindowForm;
